import { useTranslation } from "react-i18next";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { ChevronDown } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import type { UsageStatus } from "@/utils/subscription.util";

import { PlanResourceIcon } from "./PlanResourceIcon";
import { PlanUsageRing } from "./PlanUsageRing";

/** Shared with the skeleton so the header never shifts between the two. */
const PILL_SHELL =
  "flex h-9 items-center gap-2 rounded-full border bg-surface py-1 pe-2.5 ps-1 shadow-xs";

interface PlanHubButtonProps extends ComponentPropsWithoutRef<"button"> {
  /** Ring fill, 0–100. */
  percentage: number;
  status: UsageStatus;
}

/**
 * Collapsed header pill: usage ring + "Upgrade" + chevron.
 *
 * Open/closed styling reads Radix's `data-state`, so the parent only owns the
 * state it actually needs. Forwards ref and unknown props to work as a
 * `PopoverTrigger asChild`.
 */
export const PlanHubButton = forwardRef<HTMLButtonElement, PlanHubButtonProps>(
  ({ percentage, status, className, ...props }, ref) => {
    const { t } = useTranslation("common");

    return (
      <button
        ref={ref}
        type="button"
        aria-label={t("header.plan_usage")}
        className={cn(
          "group",
          PILL_SHELL,
          status === "critical" ? "border-danger/40" : "border-border",
          "transition-colors hover:border-primary/40 hover:bg-primary/5",
          "data-[state=open]:border-primary/50 data-[state=open]:bg-primary/5",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
          className,
        )}
        {...props}
      >
        <span className="relative">
          <PlanUsageRing percentage={percentage} status={status} size={28}>
            <PlanResourceIcon resource="tokens" size={15} />
          </PlanUsageRing>
          {/* Depleted resources get a dot, so the state survives a glance. */}
          {status === "critical" && (
            <span className="absolute top-0 end-0 size-2 rounded-full bg-danger ring-2 ring-surface" />
          )}
        </span>

        <span className="text-[12px] font-semibold text-foreground">
          {t("header.upgrade")}
        </span>

        <ChevronDown
          size={12}
          className="text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </button>
    );
  },
);
PlanHubButton.displayName = "PlanHubButton";

/** Same footprint as the pill, so the header doesn't shift while loading. */
export const PlanHubButtonSkeleton = ({
  className,
}: {
  className?: string;
}) => (
  <div className={cn(PILL_SHELL, "border-border", className)}>
    <Skeleton className="size-7 rounded-full" />
    <Skeleton as="span" className="h-3 w-14" />
    <Skeleton as="span" className="size-2 rounded-full" />
  </div>
);
